// Zustand store fed from CallEngine events OUTSIDE the React tree (§9.3.1).
// Components subscribe via narrow selectors; per-attendee updates replace only
// that attendee's slice object, so a speaking change re-renders exactly one
// memoized tile (§9.3.7a). Nothing in here holds a LiveKit object.

import type {
  AttendeeSnapshot,
  CallEngine,
  ChatEvent,
  E2EEState,
  FileEvent,
  RoomConnectionState,
} from '@brizina/call-engine';
import { useStore } from 'zustand';
import { useShallow } from 'zustand/react/shallow';
import { createStore } from 'zustand/vanilla';

import { playKnockSound } from '../sounds';

export interface Toast {
  id: number;
  text: string;
  tone: 'info' | 'warning' | 'error';
}

/** A pending admission request shown to the host (F-LOB-2). */
export interface KnockEntry {
  id: string;
  name: string;
  at: string;
}

/** A floating reaction; expires after ~3s (F-COM-3). */
export interface ReactionFloat {
  key: number;
  emoji: string;
  name: string;
}

export interface CallState {
  roomState: RoomConnectionState;
  disconnectReason?: string;
  localIdentity?: string;
  /** Plain serializable slices keyed by identity (§9.3.1). */
  attendees: Record<string, AttendeeSnapshot>;
  /** Stable join order — changes only on join/leave. */
  order: string[];
  /** Bumped per identity when track availability changes → attach effects re-run. */
  trackEpochs: Record<string, number>;
  /** Bumped when the hardware device set changes (plug/unplug). */
  devicesEpoch: number;
  layout: 'grid' | 'speaker';
  toasts: Toast[];
  /** Room-level E2EE badge state (F-SEC-1). */
  e2eeState: E2EEState;
  /** Unread in-meeting messages while the chat panel is closed (red dot). */
  chatUnread: number;
  /** Session-only chat history (F-COM-1) — only the chat panel subscribes,
   * which is exactly what §9.3.7(b) verifies. */
  chat: ChatEvent[];
  /** Host-side admission queue (F-LOB-2). */
  knocks: KnockEntry[];
  /** My role in the current call (F-HOST-1). isHost derives host||cohost. */
  myRole: 'host' | 'cohost' | 'attendee' | 'viewonly';
  isHost: boolean;
  reactions: ReactionFloat[];
  /** Pinned attendee identity (F-CALL-4); null = auto/active-speaker stage. */
  pinnedIdentity: string | null;
  /** Private 1:1 threads keyed by peer identity (F-COM-2). */
  privateThreads: Record<string, ChatEvent[]>;
  /** Peers with unread private messages. */
  privateUnread: Record<string, number>;
  /** Peer identities whose private-chat panel is open. */
  openPrivate: string[];
  /** Removal/end screen message, set when the host removes you (F-HOST-3/5). */
  removedReason: string | null;
  /** Room token for file-presign auth (F-COM-5). */
  roomToken: string | null;
  /** Shared files (sent + received) with per-file transfer status. */
  sharedFiles: SharedFile[];
  /** Recording indicator (F-REC-1): who is recording, or null if no one. */
  recordingBy: string | null;
  /** Org policy (Phase C): may the host record? Hides the record button when false. */
  recordingAllowed: boolean;
  /** Org policy (F-COM-5): is file sharing enabled? Hides the Files panel when false. */
  fileSharingAllowed: boolean;
  /** Meeting Tools org policy: are polls / the timer enabled? Hides the tool when false. */
  pollsAllowed: boolean;
  timerAllowed: boolean;
  /** Duration cap (Phase C): unix seconds the meeting is force-ended (0 = no cap). */
  meetingEndsAt: number;
  /** Duration cap in minutes (0 = none) — for the end message. */
  durationCapMin: number;
  /** Org-wide join-request chime id (from the grant); host plays it on a knock. */
  knockSound: string;
  /** Whether the LOCAL participant is the active recorder (host record button). */
  recordingLocally: boolean;
}

export interface SharedFile extends FileEvent {
  fromName: string;
  outgoing: boolean;
  status: 'sharing' | 'ready' | 'downloading' | 'failed';
}

const initialState: CallState = {
  roomState: 'idle',
  attendees: {},
  order: [],
  trackEpochs: {},
  devicesEpoch: 0,
  layout: 'grid',
  chatUnread: 0,
  toasts: [],
  e2eeState: 'off',
  chat: [],
  knocks: [],
  myRole: 'attendee',
  isHost: false,
  reactions: [],
  pinnedIdentity: null,
  privateThreads: {},
  privateUnread: {},
  openPrivate: [],
  removedReason: null,
  roomToken: null,
  sharedFiles: [],
  recordingBy: null,
  recordingAllowed: true,
  fileSharingAllowed: true,
  pollsAllowed: true,
  timerAllowed: true,
  meetingEndsAt: 0,
  durationCapMin: 0,
  knockSound: 'ding',
  recordingLocally: false,
};

export const callStore = createStore<CallState>(() => ({ ...initialState }));

let toastSeq = 0;

export function pushToast(text: string, tone: Toast['tone'] = 'info'): void {
  const toast = { id: ++toastSeq, text, tone };
  callStore.setState((s) => ({ toasts: [...s.toasts, toast] }));
  setTimeout(() => {
    callStore.setState((s) => ({ toasts: s.toasts.filter((t) => t.id !== toast.id) }));
  }, 4000);
}

export function setLayout(layout: 'grid' | 'speaker'): void {
  callStore.setState({ layout });
}

export function clearChatUnread(): void {
  callStore.setState({ chatUnread: 0 });
}

export function setMyRole(role: CallState['myRole']): void {
  callStore.setState({ myRole: role, isHost: role === 'host' || role === 'cohost' });
}

export function setRoomToken(token: string | null): void {
  callStore.setState({ roomToken: token });
}

export function setRecordingLocally(on: boolean): void {
  callStore.setState({ recordingLocally: on });
}

export function setRecordingAllowed(allowed: boolean): void {
  callStore.setState({ recordingAllowed: allowed });
}

export function setFileSharingAllowed(allowed: boolean): void {
  callStore.setState({ fileSharingAllowed: allowed });
}

export function setPollsAllowed(allowed: boolean): void {
  callStore.setState({ pollsAllowed: allowed });
}

export function setTimerAllowed(allowed: boolean): void {
  callStore.setState({ timerAllowed: allowed });
}

export function setKnockSound(id: string): void {
  callStore.setState({ knockSound: id || 'ding' });
}

export function setMeetingLimit(endsAt: number, capMin: number): void {
  callStore.setState({ meetingEndsAt: endsAt, durationCapMin: capMin });
}

export function addSharedFile(f: SharedFile): void {
  callStore.setState((s) => ({ sharedFiles: [...s.sharedFiles, f] }));
}

export function updateSharedFile(fileId: string, patch: Partial<SharedFile>): void {
  callStore.setState((s) => ({
    sharedFiles: s.sharedFiles.map((f) => (f.fileId === fileId ? { ...f, ...patch } : f)),
  }));
}

export function setPinned(identity: string | null): void {
  callStore.setState((s) => ({ pinnedIdentity: s.pinnedIdentity === identity ? null : identity }));
}

export function clearPrivateUnread(peer: string): void {
  callStore.setState((s) => {
    if (!s.privateUnread[peer]) return s;
    const privateUnread = { ...s.privateUnread };
    delete privateUnread[peer];
    return { privateUnread };
  });
}

export function openPrivateThread(peer: string): void {
  callStore.setState((s) =>
    s.openPrivate.includes(peer) ? s : { openPrivate: [...s.openPrivate, peer] },
  );
  clearPrivateUnread(peer);
}

export function closePrivateThread(peer: string): void {
  callStore.setState((s) => ({ openPrivate: s.openPrivate.filter((p) => p !== peer) }));
}

export function upsertKnock(k: KnockEntry): void {
  const prev = callStore.getState().knocks;
  const isNew = !prev.some((x) => x.id === k.id);
  callStore.setState({ knocks: [...prev.filter((x) => x.id !== k.id), k] });
  // Chime for a genuinely new, fresh knock — not for re-polled/old entries a
  // host sees on join (avoids a burst of sounds).
  if (isNew && Date.now() - new Date(k.at).getTime() < 15000) {
    playKnockSound(callStore.getState().knockSound);
  }
}

export function removeKnock(id: string): void {
  callStore.setState((s) => ({ knocks: s.knocks.filter((x) => x.id !== id) }));
}

// reconcileKnocks aligns the queue with the authoritative server poll: the poll
// list wins, but a WS-delivered knock younger than the poll interval is kept so
// a just-arrived knock isn't briefly dropped by a poll that snapshotted earlier.
export function reconcileKnocks(list: KnockEntry[]): void {
  callStore.setState((s) => {
    const ids = new Set(list.map((k) => k.id));
    const now = Date.now();
    const freshExtras = s.knocks.filter(
      (k) => !ids.has(k.id) && now - new Date(k.at).getTime() < 6000,
    );
    return { knocks: [...list, ...freshExtras] };
  });
}

export function clearKnocks(): void {
  callStore.setState({ knocks: [] });
}

export function resetCallState(): void {
  callStore.setState({ ...initialState }, true);
}

let reactionSeq = 0;

/** Wire engine → store once, at module init — never inside a component. */
export function bindEngineToStore(engine: CallEngine): void {
  engine.events.on('roomStateChanged', (roomState, reason) => {
    callStore.setState({
      roomState,
      disconnectReason: reason,
      localIdentity: engine.localIdentity,
    });
    if (roomState === 'reconnecting') pushToast('Connection lost — reconnecting…', 'warning');
  });

  engine.events.on('attendeeUpserted', (snap: AttendeeSnapshot) => {
    callStore.setState((s) => {
      const known = !!s.attendees[snap.identity];
      const next: Partial<CallState> = {
        attendees: { ...s.attendees, [snap.identity]: snap },
      };
      if (!known) {
        next.order = [...s.order, snap.identity];
        if (!snap.isLocal) pushToast(`${snap.name} joined`);
      }
      return next;
    });
  });

  engine.events.on('attendeeRemoved', (identity) => {
    callStore.setState((s) => {
      const gone = s.attendees[identity];
      if (gone) pushToast(`${gone.name} left`);
      const attendees = { ...s.attendees };
      delete attendees[identity];
      const trackEpochs = { ...s.trackEpochs };
      delete trackEpochs[identity];
      return { attendees, order: s.order.filter((id) => id !== identity), trackEpochs };
    });
  });

  engine.events.on('speakingChanged', (identity, speaking) => {
    callStore.setState((s) => {
      const a = s.attendees[identity];
      if (!a || a.isSpeaking === speaking) return s;
      // Replace ONLY this attendee's slice — other tiles keep object identity.
      return { attendees: { ...s.attendees, [identity]: { ...a, isSpeaking: speaking } } };
    });
  });

  engine.events.on('qualityChanged', (identity, quality) => {
    callStore.setState((s) => {
      const a = s.attendees[identity];
      if (!a || a.quality === quality) return s;
      return { attendees: { ...s.attendees, [identity]: { ...a, quality } } };
    });
  });

  engine.events.on('tracksChanged', (identity) => {
    callStore.setState((s) => ({
      trackEpochs: { ...s.trackEpochs, [identity]: (s.trackEpochs[identity] ?? 0) + 1 },
    }));
  });

  engine.events.on('mediaDevicesChanged', () => {
    callStore.setState((s) => ({ devicesEpoch: s.devicesEpoch + 1 }));
  });

  engine.events.on('e2eeStateChanged', (e2eeState) => {
    callStore.setState({ e2eeState });
    if (e2eeState === 'degraded') {
      pushToast('Encryption problem detected — some media may be unreadable', 'warning');
    }
  });

  engine.events.on('chatMessage', (message) => {
    // Append-only slice: ONLY the chat panel subscribes to s.chat (§9.3.7b).
    // A message from someone else bumps the unread dot; the open panel clears
    // it (ChatPanel), so it never shows while you're reading.
    callStore.setState((s) => ({
      chat: [...s.chat, message],
      chatUnread:
        message.fromIdentity !== s.localIdentity ? s.chatUnread + 1 : s.chatUnread,
    }));
  });

  engine.events.on('privateChat', (peer, message) => {
    callStore.setState((s) => {
      const thread = [...(s.privateThreads[peer] ?? []), message];
      const incoming = message.fromIdentity !== s.localIdentity;
      const open = s.openPrivate.includes(peer) ? s.openPrivate : [...s.openPrivate, peer];
      return {
        privateThreads: { ...s.privateThreads, [peer]: thread },
        openPrivate: open, // surface the thread (F-COM-2 visually distinct)
        privateUnread:
          incoming && !s.openPrivate.includes(peer)
            ? { ...s.privateUnread, [peer]: (s.privateUnread[peer] ?? 0) + 1 }
            : s.privateUnread,
      };
    });
  });

  engine.events.on('removed', (reason) => {
    callStore.setState({ removedReason: reason });
  });

  engine.events.on('fileShared', (_identity, fromName, ref) => {
    callStore.setState((s) => ({
      sharedFiles: [...s.sharedFiles, { ...ref, fromName, outgoing: false, status: 'ready' }],
    }));
    pushToast(`${fromName} shared ${ref.name}`);
  });

  engine.events.on('recordingChanged', (recording, byName) => {
    callStore.setState({ recordingBy: recording ? byName : null });
    if (recording) pushToast(`${byName ?? 'Someone'} started recording this meeting`, 'warning');
  });

  engine.events.on('reaction', (_identity, name, emoji) => {
    const float = { key: ++reactionSeq, emoji, name };
    callStore.setState((s) => ({ reactions: [...s.reactions, float] }));
    setTimeout(() => {
      callStore.setState((s) => ({ reactions: s.reactions.filter((r) => r.key !== float.key) }));
    }, 3000);
  });

  engine.events.on('deviceFallback', (kind, label) => {
    const what = kind === 'audioinput' ? 'Microphone' : kind === 'videoinput' ? 'Camera' : 'Speaker';
    pushToast(`${what} unplugged — switched to ${label}`, 'warning');
  });

  engine.events.on('error', (message) => pushToast(message, 'error'));
}

/** Narrow-selector hook (§9.3.1). Always select the smallest slice you need. */
export function useCallStore<T>(selector: (s: CallState) => T): T {
  return useStore(callStore, selector);
}

/**
 * For selectors that DERIVE a new array/object each run (filters, maps):
 * shallow-compares results so unrelated store updates don't re-render (§9.3).
 */
export function useCallStoreShallow<T>(selector: (s: CallState) => T): T {
  return useStore(callStore, useShallow(selector));
}
